/**
 * Export / share helpers - lightweight, no heavy deps (no jspdf/html2canvas).
 *
 * Builds a Markdown document from the in-memory analysis (threat assessment,
 * synthesis brief, battlecard) and offers three cost-light affordances:
 *   - copyMarkdown - copy a clean Markdown brief to the clipboard
 *   - downloadMarkdown - save the brief as a .md file
 *   - copyForSlack - copy a trimmed, Slack-friendly markdown-ish version
 */

import type { ThreatScore, Battlecard } from "./swarmTypes";

export interface ExportPayload {
  competitor: string;
  threat?: ThreatScore | null;
  synthesisText?: string;
  battlecard?: Battlecard | null;
  runAt?: string;
}

/** Build a full Markdown brief from the analysis payload. */
export function buildMarkdown(p: ExportPayload): string {
  const lines: string[] = [];
  const name = p.competitor || p.threat?.competitor || "Competitor";

  lines.push(`# NovaSynth Competitive Brief - ${name}`);
  if (p.runAt) {
    const when = safeDate(p.runAt);
    if (when) lines.push(`_Analyzed ${when}_`);
  }
  lines.push("");

  const t = p.threat;
  if (t) {
    lines.push(`## Threat Assessment`);
    lines.push(`- **Level:** ${t.level} (${t.score}/10, ${t.confidence}% confidence)`);
    if (t.rationale) lines.push(`- **Rationale:** ${t.rationale}`);
    if (t.whyItMattersToUs) lines.push(`- **Why it matters to us:** ${t.whyItMattersToUs}`);
    lines.push("");

    if (t.dimensions.length) {
      lines.push(`### Threat drivers`);
      for (const d of t.dimensions) {
        lines.push(`- **${d.label}** - ${d.score}/${d.max}${d.note ? `: ${d.note}` : ""}`);
      }
      lines.push("");
    }
    if (t.topThreats.length) {
      lines.push(`### Top threats`);
      for (const x of t.topThreats) lines.push(`- ${x}`);
      lines.push("");
    }
    if (t.counterMoves.length) {
      lines.push(`### Counter-moves`);
      for (const x of t.counterMoves) lines.push(`- ${x}`);
      lines.push("");
    }
  }

  if (p.synthesisText && p.synthesisText.trim()) {
    lines.push(`## Intelligence Brief`);
    lines.push(p.synthesisText.trim());
    lines.push("");
  }

  if (p.battlecard && p.battlecard.markdown.trim()) {
    lines.push(`## Battlecard`);
    lines.push(p.battlecard.markdown.trim());
    lines.push("");
  }

  if (t && t.sources.length) {
    lines.push(`## Sources`);
    for (const s of t.sources) lines.push(`- ${s}`);
    lines.push("");
  }

  return lines.join("\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";
}

/**
 * A trimmed, Slack-friendly variant. Slack renders a markdown-ish subset, so we
 * keep bold (*…*), bullets, and links but drop heavy headings and the long brief
 * body - the score, the why, and the moves are what a channel needs.
 */
export function buildSlackText(p: ExportPayload): string {
  const lines: string[] = [];
  const name = p.competitor || p.threat?.competitor || "Competitor";
  const t = p.threat;

  lines.push(`*Competitive brief - ${name}*`);
  if (t) {
    lines.push(`Threat: *${t.level}* (${t.score}/10, ${t.confidence}% confidence)`);
    if (t.rationale) lines.push(`${t.rationale}`);
    if (t.whyItMattersToUs) lines.push(`*Why it matters to us:* ${t.whyItMattersToUs}`);
    if (t.topThreats.length) {
      lines.push("", "*Top threats*");
      for (const x of t.topThreats) lines.push(`• ${x}`);
    }
    if (t.counterMoves.length) {
      lines.push("", "*Counter-moves*");
      for (const x of t.counterMoves) lines.push(`• ${x}`);
    }
  } else if (p.synthesisText) {
    lines.push(p.synthesisText.slice(0, 1500));
  }
  lines.push("", "_via NovaSynth_");
  return lines.join("\n");
}

/** Copy arbitrary text to the clipboard. Returns true on success. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function copyMarkdown(p: ExportPayload): Promise<boolean> {
  return copyText(buildMarkdown(p));
}

export function copyForSlack(p: ExportPayload): Promise<boolean> {
  return copyText(buildSlackText(p));
}

/** Trigger a .md file download for the brief. */
export function downloadMarkdown(p: ExportPayload): void {
  const md = buildMarkdown(p);
  const name = (p.competitor || p.threat?.competitor || "competitor")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const filename = `novasynth-${name || "brief"}-${ymd()}.md`;
  const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a tick so the download is registered first.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function ymd(d: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
}

function safeDate(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  try {
    return new Date(t).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}
